import Decimal from 'decimal.js';
import { http, HttpResponse, type HttpHandler } from 'msw';

import { devCardHandlers } from './cardHandlers';

/**
 * DEV-ONLY mock backend for the wallet, deposit and money-out screens.
 *
 * These handlers are how the flows are developed and walked end to end without a
 * backend. They are loaded only when `VITE_ENABLE_MOCKS=true`, and never in a
 * production build — see `src/mocks/browser.ts`.
 *
 * They are also a second copy of the contract, which is exactly the kind of copy
 * that drifts in silence, so `devHandlers.smoke.test.ts` runs the REAL wire
 * modules against them.
 *
 * Everything answers in the standard envelope, and every amount is a DECIMAL
 * STRING, because that is what the real API must send. If a mock returned a JS
 * number the app would type-check against a shape the backend does not have.
 *
 * The deposits list deliberately contains a PENDING row that flips to confirmed
 * after ~40 seconds, so the polling and the pending badge can be observed
 * without a chain.
 */

const envelope = <T>(result: T) => ({ isSuccess: true, error: null, errorCode: null, result });

const ADDRESSES = {
  trc20: 'TQn9Y2khEsLJW1ChVWFMSMeRDow5KcbLSE',
  bsc: '0x8894E0a0c962CB723c1976a4421c95949bE2D4E3'
} as const;

const startedAt = Date.now();
/** How long the seeded pending deposit stays pending, in ms. */
const PENDING_FOR_MS = 40_000;

function deposits() {
  const settled = Date.now() - startedAt > PENDING_FOR_MS;
  return [
    {
      id: 'dep_3',
      network: 'trc20',
      amount: '25.000000',
      fee: '0.000000',
      netAmount: '25.000000',
      currency: 'USDT-TRC20',
      status: settled ? 'Confirmed' : 'Pending',
      txId: settled ? '9f2c1b0a7d4e6f8a1c3b5d7e9f0a2c4b6d8e0f1a3c5b7d9e1f3a5c7b9d0e2f4a' : null,
      confirmedAt: settled ? new Date().toISOString() : null,
      createdAt: new Date(startedAt).toISOString()
    },
    {
      id: 'dep_2',
      network: 'bsc',
      // A row from before the provider stopped charging. It stays in the seed
      // data on purpose: the history has to keep showing a real fee while the
      // deposit screen says deposits are free, and this is the row that proves
      // the list does not simply assume zero.
      amount: '150.000000',
      fee: '0.500000',
      netAmount: '149.500000',
      currency: 'USDT-BEP20',
      status: 'Confirmed',
      txId: '0x4c1f8a2e6b0d9c3a7e5f1b8d2c6a0e4f9b3d7c1a5e8f2b6d0c4a8e2f6b0d4c8a',
      confirmedAt: new Date(Date.now() - 3_600_000).toISOString(),
      createdAt: new Date(Date.now() - 3_900_000).toISOString()
    },
    {
      id: 'dep_1',
      network: 'trc20',
      amount: '10.000000',
      fee: '0.000000',
      netAmount: '10.000000',
      currency: 'USDT-TRC20',
      status: 'Failed',
      txId: null,
      confirmedAt: null,
      createdAt: new Date(Date.now() - 86_400_000).toISOString()
    }
  ];
}

/* -------------------------------------------------- money-out mock state */

/**
 * In-memory rows, so a withdrawal you create in dev appears in the history and
 * then settles — which is the only way to see the pending panel do its job.
 */
const withdrawals: Record<string, unknown>[] = [];
const payouts: Record<string, unknown>[] = [];
const transfers: Record<string, unknown>[] = [];

let idCounter = 1000;
function nextId(): number {
  idCounter += 1;
  return idCounter;
}

/** Decimal maths even in a mock: a mock that emits float artefacts teaches the UI to expect them. */
function decimalSum(values: readonly string[]): string {
  return values
    .reduce((total, value) => total.plus(new Decimal(value || '0')), new Decimal(0))
    .toFixed(6);
}

/**
 * The rouble payout rate the mock quotes, already carrying the Novix spread.
 *
 * The real backend takes the payment partner's effective rate, worsens it by
 * the configured 3 %, and rounds the result DOWN to kopecks. The numbers here
 * are the live ones rather than a round invention: Walet quotes 78.25 through
 * Okline, and 78.25 × 0.97 = 75.9025, which becomes 75.90. There is no separate
 * commission field to mock because there is no separate commission; the margin
 * is the gap between this rate and the partner's.
 *
 * Using the real pair costs nothing and means a screen in development shows the
 * same magnitudes support will be asked about.
 */
const PAYOUT_RATE = '75.90';

/**
 * What the customer is debited for `amountRub`, computed the way the backend
 * computes it: from the ROUNDED rate above, rounded UP, to TWO places.
 *
 * All three details matter even in a mock. Dividing by the partner's unrounded
 * rate would produce a figure that disagrees with the rate on screen; rounding
 * down would teach the UI to expect a debit that does not cover the payout; and
 * six places would be the subtlest of the three — `formatMoney` renders USDT to
 * two and rounds DOWN to get there, so a mocked 68.728523 reaches the screen as
 * 68,72 while the rate beside it says the answer is 68,73. The backend charges
 * at the displayed scale precisely so that cannot happen
 * (`RubPayoutPricing.DebitScale`), and a mock that kept six places would show a
 * broken receipt in development that production cannot produce — the mock
 * hiding the truth, which is how the base-URL outage happened.
 */
function payoutDebit(amountRub: string): string {
  return new Decimal(amountRub || '0').div(PAYOUT_RATE).toFixed(2, Decimal.ROUND_UP);
}

/** How long a mocked operation stays `Reserved` before it completes, in ms. */
const SETTLE_AFTER_MS = 25_000;

/**
 * Moves a row to `Completed` after a while, so the pending → completed
 * transition, the polling and the "sent" wording can all be observed without a
 * chain or a bank.
 */
function settleLater(row: Record<string, unknown>): void {
  setTimeout(() => {
    row['status'] = 'Completed';
    row['completedAt'] = new Date().toISOString();
    if ('networkFeeUsdt' in row) row['actualFeeUsdt'] = row['networkFeeUsdt'];
    if ('toAddress' in row) row['txId'] = `0x${nextId().toString(16).padStart(8, '0')}`;
  }, SETTLE_AFTER_MS);
}

/**
 * The dev account. Its `id` is the same 1000042 the transfer mocks accept as a
 * recipient, so "copy my id" and "send to that id" line up when the flows are
 * walked back to back.
 */
const DEV_PROFILE = {
  id: 1000042,
  email: 'dev@novix.local',
  emailConfirmed: true,
  status: 'Active',
  createdAt: '2025-01-01T09:00:00Z',
  lastLoginAt: new Date().toISOString()
};

/** The dev account's language, changed by `PUT /account/language`. */
let devLanguage = 'ru';

/**
 * Stands in for the httpOnly refresh cookie.
 *
 * The mock has to remember whether this browser has signed in, or `/auth/refresh`
 * would hand a session to anyone who opened the app — which would make the gate
 * untestable by hand and, worse, make "sign out" look broken. `localStorage`
 * because the real credential also survives a reload; this is a dev fixture and
 * never ships (see browser.ts).
 */
const DEV_SESSION_KEY = 'novix.dev.session';

function devSignedIn(): boolean {
  try {
    return globalThis.localStorage.getItem(DEV_SESSION_KEY) === '1';
  } catch {
    return false;
  }
}

function setDevSignedIn(value: boolean): void {
  try {
    if (value) globalThis.localStorage.setItem(DEV_SESSION_KEY, '1');
    else globalThis.localStorage.removeItem(DEV_SESSION_KEY);
  } catch {
    /* storage disabled — the dev fixture simply will not persist */
  }
}

function devTokens() {
  return {
    accessToken: 'dev-access-token',
    accessTokenExpiresAt: new Date(Date.now() + 15 * 60_000).toISOString(),
    refreshToken: 'dev-refresh-token',
    refreshTokenExpiresAt: new Date(Date.now() + 30 * 86_400_000).toISOString()
  };
}

/** An error envelope, matching the shape `client.ts` unwraps. */
function fail(errorCode: string, error: string, status = 400) {
  return HttpResponse.json({ isSuccess: false, error, errorCode, result: null }, { status });
}

/* ------------------------------------------------- the two address books */

interface DevContact {
  id: number;
  targetUserId: number;
  label: string;
  createdAt: string;
  updatedAt: string;
}

interface DevCard {
  id: number;
  maskedCardNumber: string;
  label: string;
  createdAt: string;
  updatedAt: string;
  /**
   * Dev-only. The REAL `SavedCardView` has no such field — a card's readability
   * is knowable only when a payout tries to decrypt it — so this never reaches a
   * response body. It exists so the unreadable path can be walked by hand.
   */
  unreadable: boolean;
}

/** Fingerprints, so the duplicate check can be exercised without storing PANs. */
const cardFingerprints = new Map<number, string>();

const devContacts: DevContact[] = [
  {
    id: 9001,
    targetUserId: 1000043,
    label: 'Аня — аренда',
    createdAt: '2025-02-01T10:00:00Z',
    updatedAt: '2025-02-01T10:00:00Z'
  },
  {
    id: 9002,
    targetUserId: 1000044,
    label: 'Sergey (work)',
    createdAt: '2025-02-03T12:30:00Z',
    updatedAt: '2025-02-03T12:30:00Z'
  }
];

const devCards: DevCard[] = [
  {
    id: 8001,
    maskedCardNumber: '427638••••5678',
    label: 'Зарплатная',
    createdAt: '2025-02-01T10:00:00Z',
    updatedAt: '2025-02-01T10:00:00Z',
    unreadable: false
  },
  {
    /*
     * Seeded UNREADABLE, so the key-ring-loss path is walkable in dev. Paying to
     * it answers `payout_saved_card_unreadable`, which is the only way the UI can
     * discover the state — exactly as in production.
     */
    id: 8002,
    maskedCardNumber: '220220••••0002',
    // Backfilled from the mask, as the migration did for cards saved before the
    // name was required.
    label: 'Карта ••0002',
    createdAt: '2025-01-10T08:00:00Z',
    updatedAt: '2025-01-10T08:00:00Z',
    unreadable: true
  }
];

cardFingerprints.set(8001, '4276380012345678');
cardFingerprints.set(8002, '2202200000000002');

/** The view the API actually returns — note `unreadable` is not on it. */
function cardView(card: DevCard) {
  const { unreadable: _unreadable, ...view } = card;
  return view;
}

function maskCard(digits: string): string {
  return `${digits.slice(0, 6)}••••${digits.slice(-4)}`;
}

function luhnOk(digits: string): boolean {
  if (digits.length < 13 || digits.length > 19) return false;
  let sum = 0;
  let double = false;
  for (let index = digits.length - 1; index >= 0; index -= 1) {
    let digit = digits.charCodeAt(index) - 48;
    if (double) {
      digit *= 2;
      if (digit > 9) digit -= 9;
    }
    sum += digit;
    double = !double;
  }
  return sum % 10 === 0;
}

/** Rate-limit the mocked save after this many calls, to exercise the 429 path. */
const CARD_SAVE_BUDGET = 6;
let cardSaveCalls = 0;

export const devHandlers: HttpHandler[] = [
  /* The card product lives in its own file — see `cardHandlers.ts`. */
  ...devCardHandlers,

  /*
   * ── TRANSFER CONTACTS ────────────────────────────────────────────────────
   *
   * The list answers a BARE ARRAY, as the real endpoint does — not a paged
   * envelope. Getting that wrong in the mock is precisely the drift the smoke
   * test exists to catch, since the real wire module is run against these.
   */
  http.get('*/api/v1/transfers/contacts', () => HttpResponse.json(envelope(devContacts))),

  http.post('*/api/v1/transfers/contacts', async ({ request }) => {
    const body = (await request.json()) as { targetUserId?: number; label?: string | null };
    const targetUserId = Number(body.targetUserId ?? 0);
    const label = (body.label ?? '').trim();

    // Self before anything else, matching the handler's order.
    if (targetUserId === DEV_PROFILE.id) {
      return fail('transfer_contact_self', 'You cannot add yourself as a contact.');
    }
    if (label === '' || label.length > 64) {
      return fail('transfer_contact_label_invalid', 'A contact name is required.');
    }
    if (devContacts.some((row) => row.targetUserId === targetUserId)) {
      return fail('transfer_contact_already_exists', 'That user is already in your contacts.');
    }

    const now = new Date().toISOString();
    const row: DevContact = {
      id: nextId(),
      targetUserId,
      label,
      createdAt: now,
      updatedAt: now
    };
    devContacts.unshift(row);
    return HttpResponse.json(envelope(row));
  }),

  http.get('*/api/v1/transfers/contacts/:contactId', ({ params }) => {
    const row = devContacts.find((item) => String(item.id) === String(params['contactId']));
    return row === undefined
      ? fail('transfer_contact_not_found', 'No such contact.', 404)
      : HttpResponse.json(envelope(row));
  }),

  http.put('*/api/v1/transfers/contacts/:contactId', async ({ params, request }) => {
    const body = (await request.json()) as { label?: string | null };
    const label = (body.label ?? '').trim();
    const row = devContacts.find((item) => String(item.id) === String(params['contactId']));

    if (row === undefined) return fail('transfer_contact_not_found', 'No such contact.', 404);
    if (label === '' || label.length > 64) {
      return fail('transfer_contact_label_invalid', 'A contact name is required.');
    }

    row.label = label;
    row.updatedAt = new Date().toISOString();
    return HttpResponse.json(envelope(row));
  }),

  http.delete('*/api/v1/transfers/contacts/:contactId', ({ params }) => {
    const index = devContacts.findIndex((item) => String(item.id) === String(params['contactId']));
    if (index === -1) return fail('transfer_contact_not_found', 'No such contact.', 404);
    devContacts.splice(index, 1);
    return HttpResponse.json(envelope(null));
  }),

  /*
   * ── SAVED CARDS ──────────────────────────────────────────────────────────
   *
   * `POST` is the only route that accepts a number, and no response here
   * carries one — `cardView` drops even the dev-only `unreadable` flag, so the
   * mock cannot accidentally hand the client a field production does not have.
   */
  http.get('*/api/v1/payouts/rub/cards', () => HttpResponse.json(envelope(devCards.map(cardView)))),

  http.post('*/api/v1/payouts/rub/cards', async ({ request }) => {
    cardSaveCalls += 1;
    // Metered like a payment, because the real route is.
    if (cardSaveCalls > CARD_SAVE_BUDGET) {
      return HttpResponse.json(
        { isSuccess: false, error: 'Too many requests.', errorCode: null, result: null },
        { status: 429, headers: { 'Retry-After': '30' } }
      );
    }

    const body = (await request.json()) as {
      cardNumber?: string;
      label?: string | null;
    };
    const digits = (body.cardNumber ?? '').replace(/\D/g, '');
    const label = (body.label ?? '')?.trim() ?? '';

    if (!luhnOk(digits)) {
      return fail('payout_invalid_card_number', 'That card number does not look right.');
    }
    // Missing, blank and over-long all answer with the one code, as the real
    // handler does since the name became required.
    if (label === '' || label.length > 64) {
      return fail('payout_invalid_card_label', 'A card needs a name of at most 64 characters.');
    }
    if ([...cardFingerprints.values()].includes(digits)) {
      return fail('payout_saved_card_already_exists', 'That card is already saved.');
    }

    const now = new Date().toISOString();
    const row: DevCard = {
      id: nextId(),
      // The mask is all that is retained. The digits go into the fingerprint
      // map for the duplicate check and are not stored on the row.
      maskedCardNumber: maskCard(digits),
      label,
      createdAt: now,
      updatedAt: now,
      unreadable: false
    };
    devCards.unshift(row);
    cardFingerprints.set(row.id, digits);
    return HttpResponse.json(envelope(cardView(row)));
  }),

  http.get('*/api/v1/payouts/rub/cards/:cardId', ({ params }) => {
    const row = devCards.find((item) => String(item.id) === String(params['cardId']));
    return row === undefined
      ? fail('payout_saved_card_not_found', 'No such saved card.', 404)
      : HttpResponse.json(envelope(cardView(row)));
  }),

  http.put('*/api/v1/payouts/rub/cards/:cardId', async ({ params, request }) => {
    const body = (await request.json()) as { label?: string | null };
    const row = devCards.find((item) => String(item.id) === String(params['cardId']));
    if (row === undefined) return fail('payout_saved_card_not_found', 'No such saved card.', 404);

    // Blank is a refusal, not "leave it alone". There is no clear flag: the name
    // is the only editable field and it is required.
    const label = (body.label ?? '').trim();
    if (label === '' || label.length > 64) {
      return fail('payout_invalid_card_label', 'A card needs a name of at most 64 characters.');
    }
    row.label = label;

    row.updatedAt = new Date().toISOString();
    return HttpResponse.json(envelope(cardView(row)));
  }),

  http.delete('*/api/v1/payouts/rub/cards/:cardId', ({ params }) => {
    const index = devCards.findIndex((item) => String(item.id) === String(params['cardId']));
    if (index === -1) return fail('payout_saved_card_not_found', 'No such saved card.', 404);
    cardFingerprints.delete(devCards[index]!.id);
    devCards.splice(index, 1);
    return HttpResponse.json(envelope(null));
  }),

  /*
   * AUTH. Added because the whole application is behind the gate now: without a
   * sign-in and a `/account/me` that answer, `VITE_ENABLE_MOCKS=true` produces
   * an app whose only reachable screen is /welcome.
   *
   * `/auth/refresh` answers successfully, which is what makes a page reload
   * behave the way it does against the real backend — the access token is gone
   * from memory and the session is restored from the (mock) cookie.
   */
  http.post('*/api/v1/auth/login', () => {
    setDevSignedIn(true);
    return HttpResponse.json(
      envelope({
        tokens: devTokens()
      })
    );
  }),

  http.post('*/api/v1/auth/refresh', () => {
    // 401 when this browser has never signed in — otherwise the gate would let
    // every visitor straight through and there would be nothing to test.
    if (!devSignedIn()) {
      return HttpResponse.json(
        { isSuccess: false, error: 'no session', errorCode: 'invalid_token', result: null },
        { status: 401 }
      );
    }
    return HttpResponse.json(
      envelope({
        accessToken: 'dev-access-token',
        accessTokenExpiresAt: new Date(Date.now() + 15 * 60_000).toISOString(),
        newRefreshToken: 'dev-refresh-token'
      })
    );
  }),

  http.post('*/api/v1/auth/logout', () => {
    setDevSignedIn(false);
    return HttpResponse.json(envelope(null));
  }),

  http.get('*/api/v1/account/me', () =>
    devSignedIn()
      ? HttpResponse.json(envelope({ ...DEV_PROFILE, language: devLanguage }))
      : HttpResponse.json(
          { isSuccess: false, error: 'no session', errorCode: 'invalid_token', result: null },
          { status: 401 }
        )
  ),

  http.put('*/api/v1/account/language', async ({ request }) => {
    const body = (await request.json()) as { language?: string };
    devLanguage = body.language ?? devLanguage;
    return HttpResponse.json(envelope(null));
  }),

  http.get('*/api/v1/crypto/networks', () =>
    HttpResponse.json(
      envelope([
        {
          network: 'trc20',
          currency: 'USDT',
          depositFeeUsdt: '0.000000',
          withdrawalFeeUsdt: '3.000000',
          minimumWithdrawalUsdt: '10.000000'
        },
        {
          network: 'bsc',
          currency: 'USDT',
          depositFeeUsdt: '0.000000',
          withdrawalFeeUsdt: '2.000000',
          minimumWithdrawalUsdt: '10.000000'
        }
      ])
    )
  ),

  // The price list. Same figures as production's tariff and configuration.
  http.get('*/api/v1/crypto/withdrawals/terms', () =>
    HttpResponse.json(
      envelope({
        commissionPercent: '1',
        commissionFixedUsdt: '1.000000',
        commissionMinimumUsdt: '1.000000',
        commissionMaximumUsdt: '100.000000',
        dailyLimitUsdt: '10000.000000',
        exampleAmountUsdt: '100.000000',
        exampleCommissionUsdt: '2.000000',
        networks: [
          {
            network: 'trc20',
            networkLabel: 'Tron (TRC20)',
            networkFeeUsdt: '3.750000',
            minimumAmountUsdt: '10.000000',
            exampleTotalDebitUsdt: '105.750000'
          },
          {
            network: 'bsc',
            networkLabel: 'BNB Smart Chain (BEP20)',
            networkFeeUsdt: '2.500000',
            minimumAmountUsdt: '10.000000',
            exampleTotalDebitUsdt: '104.500000'
          }
        ]
      })
    )
  ),

  http.get('*/api/v1/payouts/rub/limits', () =>
    HttpResponse.json(envelope({ minimumAmountRub: '5000', rateAdjustmentPercent: '3' }))
  ),

  http.get('*/api/v1/crypto/deposit-address', ({ request }) => {
    const network = new URL(request.url).searchParams.get('network') === 'bsc' ? 'bsc' : 'trc20';
    return HttpResponse.json(
      envelope({
        address: ADDRESSES[network],
        network,
        // The provider's ticker, suffix and all — exactly what the UI must
        // never render verbatim. Keeping it ugly here keeps the sanitiser honest.
        currency: network === 'bsc' ? 'USDT-BEP20' : 'USDT-TRC20',
        // 1 USDT on both rails, verified against the live provider — and served
        // from the API, not hardcoded in the frontend.
        minimumAmount: '1.000000',
        // Zero, and STATED rather than omitted: the deposit screen renders
        // "free" from this figure. Omitting it would make the screen say
        // nothing at all, which is what an older backend looks like — a
        // different answer from "the provider charges nothing".
        depositFeeUsdt: '0.000000'
      })
    );
  }),

  http.get('*/api/v1/crypto/deposits', () => HttpResponse.json(envelope(deposits()))),

  http.get('*/api/v1/wallet/balance', () =>
    HttpResponse.json(envelope({ available: '184.500000', reserved: '0.000000', currency: 'USDT' }))
  ),

  /* ------------------------------------------------------------ money out */

  /**
   * Step-up, so the money flows can actually be walked in dev.
   *
   * Accepts ANY six digits. It exists to let the dialog resolve, not to model
   * TOTP — a mock that validated codes would be a mock that could lock you out
   * of your own dev environment.
   */

  /**
   * The crypto quote.
   *
   * The fee figures are the ones that motivated this screen: TRON costs about
   * 3 USDT and BSC about 0.6, both charged ON TOP of the amount. Note that
   * `maxWithdrawableUsdt` is the balance MINUS those, never the balance — a
   * "max" wired to the raw balance would fail every time.
   */
  http.post('*/api/v1/crypto/withdrawals/quote', async ({ request }) => {
    const body = (await request.json()) as { network?: string; amount?: string };
    const bsc = (body.network ?? '').toLowerCase() === 'bsc';
    const fee = bsc ? '0.600000' : '3.000000';
    const commission = '0.500000';
    const balance = '184.500000';
    const amount = body.amount ?? '0';
    const total = decimalSum([amount, fee, commission]);

    return HttpResponse.json(
      envelope({
        network: bsc ? 'Bsc' : 'Trc20',
        amountUsdt: amount,
        networkFeeUsdt: fee,
        commissionUsdt: commission,
        totalDebitUsdt: total,
        minimumAmountUsdt: bsc ? '5.000000' : '10.000000',
        maxWithdrawableUsdt: decimalSum([balance, `-${fee}`, `-${commission}`]),
        availableBalanceUsdt: balance,
        remainingDailyLimitUsdt: '5000.000000'
      })
    );
  }),

  http.post('*/api/v1/crypto/withdrawals', async ({ request }) => {
    const body = (await request.json()) as {
      network?: string;
      toAddress?: string;
      amount?: string;
    };
    const bsc = (body.network ?? '').toLowerCase() === 'bsc';
    const row = {
      id: nextId(),
      // Reserved, not Sent. Debited, queued, nothing on chain — which is the
      // whole point of the pending panel on the screen.
      status: 'Reserved',
      network: bsc ? 'Bsc' : 'Trc20',
      toAddress: body.toAddress ?? '',
      amountUsdt: body.amount ?? '0',
      networkFeeUsdt: bsc ? '0.600000' : '3.000000',
      actualFeeUsdt: null,
      commissionUsdt: '0.500000',
      totalDebitUsdt: decimalSum([body.amount ?? '0', bsc ? '0.600000' : '3.000000', '0.500000']),
      txId: null,
      createdAt: new Date().toISOString(),
      completedAt: null,
      failureReason: null
    };
    withdrawals.unshift(row);
    settleLater(row);
    return HttpResponse.json(envelope(row));
  }),

  http.get('*/api/v1/crypto/withdrawals', () => HttpResponse.json(envelope(withdrawals))),

  /** The rouble preview. Refuses below 5 000 ₽ exactly as the backend does. */
  http.post('*/api/v1/payouts/rub/preview', async ({ request }) => {
    const body = (await request.json()) as { amountRub?: string };
    const amountRub = body.amountRub ?? '0';
    if (Number(amountRub) < 5000) {
      return HttpResponse.json(
        {
          isSuccess: false,
          error: 'The minimum payout is 5 000 ₽.',
          errorCode: 'payout_below_minimum',
          result: null
        },
        { status: 400 }
      );
    }
    return HttpResponse.json(
      envelope({
        amountRub,
        amountUsdt: payoutDebit(amountRub),
        rate: PAYOUT_RATE,
        minimumAmountRub: '5000.00',
        expiresAt: new Date(Date.now() + 20_000).toISOString()
      })
    );
  }),

  http.post('*/api/v1/payouts/rub', async ({ request }) => {
    const body = (await request.json()) as {
      amountRub?: string;
      cardNumber?: string;
      savedCardId?: number;
    };

    /*
     * The one-of-two rule, refused rather than resolved by precedence — the
     * same way the real handler does it, so a client bug that sends both fails
     * here too instead of quietly paying whichever card the mock preferred.
     */
    const hasRaw = (body.cardNumber ?? '').trim() !== '';
    const hasSaved = body.savedCardId !== undefined && body.savedCardId !== null;

    if (hasRaw && hasSaved) {
      return fail('payout_card_ambiguous', 'Supply a card number or a saved card id, not both.');
    }
    if (!hasRaw && !hasSaved) {
      return fail('payout_card_required', 'Supply either a card number or a saved card id.');
    }

    let maskedCardNumber: string;

    if (hasSaved) {
      const card = devCards.find((item) => item.id === body.savedCardId);
      if (card === undefined) {
        return fail('payout_saved_card_not_found', 'No such saved card.', 404);
      }
      if (card.unreadable) {
        return fail(
          'payout_saved_card_unreadable',
          'That saved card can no longer be read. Please remove it and save it again.'
        );
      }
      // Off the STORED row, exactly as `ResolveCardAsync` resolves it.
      maskedCardNumber = card.maskedCardNumber;
    } else {
      const digits = (body.cardNumber ?? '').replace(/\D/g, '');
      maskedCardNumber = `•••• ${digits.slice(-4)}`;
    }

    const row = {
      id: nextId(),
      status: 'Reserved',
      amountRub: body.amountRub ?? '0',
      amountUsdt: payoutDebit(body.amountRub ?? '0'),
      rate: PAYOUT_RATE,
      // Masked here as the backend masks it. The full number is not echoed and
      // is not retained by this mock either.
      maskedCardNumber,
      createdAt: new Date().toISOString(),
      completedAt: null,
      failureReason: null
    };
    payouts.unshift(row);
    settleLater(row);
    return HttpResponse.json(envelope(row));
  }),

  http.get('*/api/v1/payouts/rub', () => HttpResponse.json(envelope(payouts))),

  http.post('*/api/v1/transfers/preview', async ({ request }) => {
    const body = (await request.json()) as { recipientUserId?: number; amountUsdt?: string };
    const amount = body.amountUsdt ?? '0';
    const fee = (Number(amount) * 0.01).toFixed(6);
    const total = decimalSum([amount, fee]);
    return HttpResponse.json(
      envelope({
        recipientUserId: body.recipientUserId ?? 0,
        amountUsdt: amount,
        feeUsdt: fee,
        totalDebitUsdt: total,
        availableBalanceUsdt: '184.500000',
        minimumAmountUsdt: '1.000000',
        hasSufficientFunds: Number(total) <= 184.5
      })
    );
  }),

  http.post('*/api/v1/transfers', async ({ request }) => {
    const body = (await request.json()) as {
      recipientUserId?: number;
      contactId?: number;
      amountUsdt?: string;
      note?: string;
    };

    // Exactly one, refused rather than resolved — as the real handler does.
    const hasRecipient = body.recipientUserId !== undefined && body.recipientUserId !== null;
    const hasContact = body.contactId !== undefined && body.contactId !== null;

    if (hasRecipient && hasContact) {
      return fail(
        'transfer_recipient_ambiguous',
        'Supply a recipient user id or a saved contact id, not both.'
      );
    }
    if (!hasRecipient && !hasContact) {
      return fail('transfer_recipient_required', 'Supply either a recipient id or a contact id.');
    }

    let counterpartyUserId: number;

    if (hasContact) {
      const contact = devContacts.find((item) => item.id === body.contactId);
      if (contact === undefined) return fail('transfer_contact_not_found', 'No such contact.', 404);
      // Resolved server-side from the caller's own book, never from the client.
      counterpartyUserId = contact.targetUserId;
    } else {
      counterpartyUserId = body.recipientUserId ?? 0;
    }

    const amount = body.amountUsdt ?? '0';
    const fee = (Number(amount) * 0.01).toFixed(6);
    const row = {
      id: nextId(),
      direction: 'Outgoing',
      counterpartyUserId,
      amountUsdt: amount,
      feeUsdt: fee,
      totalDebitUsdt: decimalSum([amount, fee]),
      note: body.note ?? null,
      createdAt: new Date().toISOString()
    };
    transfers.unshift(row);
    return HttpResponse.json(envelope(row));
  }),

  http.get('*/api/v1/transfers', () => HttpResponse.json(envelope(transfers)))
];
